import { httpClient } from '../core/http';

export interface AgentExecutionOrder {
  agent_id: number;
  execution_order: number;
}

export interface CrewOrderResponse {
  crew_id: number;
  agent_orders: AgentExecutionOrder[];
}

class CrewOrderService {
  /**
   * Obter a ordem atual de execução dos agentes em um crew
   */
  async getAgentExecutionOrder(crewId: number): Promise<AgentExecutionOrder[]> {
    try {
      const response = await httpClient.get<{
        crew_id: number;
        agents_order: Array<{
          crew_agent_id: number;
          agent_id: number;
          agent_name: string;
          agent_role: string;
          execution_order: number;
        }>;
        message: string;
      }>(`/v1/crews/${crewId}/agents/order`);

      // Converter o formato da API para o formato esperado
      const agentsOrder = response.data.agents_order.map(item => ({
        agent_id: item.agent_id,
        execution_order: item.execution_order,
      }));

      return agentsOrder;
    } catch (error) {
      console.error('Erro ao obter ordem de execução dos agentes:', error);
      throw error;
    }
  }

  /**
   * Atualizar a ordem de execução dos agentes em um crew
   */
  async updateAgentExecutionOrder(
    crewId: number,
    agentOrders: AgentExecutionOrder[]
  ): Promise<AgentExecutionOrder[]> {
    try {
      const response = await httpClient.put<AgentExecutionOrder[]>(
        `/v1/crews/${crewId}/agents/order`,
        agentOrders
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar ordem de execução dos agentes:', error);
      throw error;
    }
  }

  /**
   * Mover um agente para uma nova posição na ordem de execução
   */
  async moveAgentToPosition(
    crewId: number,
    agentId: number,
    newPosition: number
  ): Promise<AgentExecutionOrder[]> {
    try {
      const response = await httpClient.post<AgentExecutionOrder[]>(
        `/v1/crews/${crewId}/agents/${agentId}/move?new_position=${newPosition}`
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao mover agente para nova posição:', error);
      throw error;
    }
  }

  /**
   * Calcular ordem de execução baseada nas conexões do workflow
   * Implementa algoritmo de ordenação topológica
   */
  calculateExecutionOrder(
    agents: Array<{ id: string; agentId?: number }>,
    connections: Array<{ from: string; to: string }>
  ): AgentExecutionOrder[] {
    // Criar mapa de dependências
    const dependencies = new Map<string, Set<string>>();
    const inDegree = new Map<string, number>();

    // Inicializar graus de entrada
    agents.forEach(agent => {
      inDegree.set(agent.id, 0);
      dependencies.set(agent.id, new Set());
    });

    // Construir grafo de dependências
    connections.forEach(connection => {
      const from = connection.from;
      const to = connection.to;

      if (dependencies.has(from) && dependencies.has(to)) {
        dependencies.get(from)!.add(to);
        inDegree.set(to, (inDegree.get(to) || 0) + 1);
      }
    });

    // Algoritmo de ordenação topológica (Kahn's algorithm)
    const queue: string[] = [];
    const result: AgentExecutionOrder[] = [];

    // Encontrar nós sem dependências (grau de entrada = 0)
    agents.forEach(agent => {
      if (inDegree.get(agent.id) === 0) {
        queue.push(agent.id);
      }
    });

    let order = 0;
    while (queue.length > 0) {
      const current = queue.shift()!;
      const agent = agents.find(a => a.id === current);

      if (agent && agent.agentId) {
        result.push({
          agent_id: agent.agentId,
          execution_order: order++,
        });
      }

      // Processar dependências do nó atual
      dependencies.get(current)?.forEach(dependent => {
        const newInDegree = (inDegree.get(dependent) || 0) - 1;
        inDegree.set(dependent, newInDegree);

        if (newInDegree === 0) {
          queue.push(dependent);
        }
      });
    }

    // Se ainda há nós não processados, há um ciclo
    if (result.length !== agents.length) {
      console.warn('Ciclo detectado no workflow. Ordem parcial calculada.');

      // Adicionar agentes restantes no final
      agents.forEach(agent => {
        if (agent.agentId && !result.find(r => r.agent_id === agent.agentId)) {
          result.push({
            agent_id: agent.agentId,
            execution_order: order++,
          });
        }
      });
    }

    return result;
  }
}

export const crewOrderService = new CrewOrderService();
